/**
 * Cascade Tracing API Service
 *
 * This file contains TypeScript API functions for cascade-tracing routes
 * that have been migrated from the legacy JavaScript API.
 *
 * These functions point to the new `/cascade-tracing/` endpoints on the backend.
 * Note: The fetch wrapper prepends the backend URL, so paths should NOT include /api/ prefix.
 *
 * IMPORTANT: These routes now query the STYLES table instead of the legacy PRODUCTS table.
 * See migration: 202601261000-add-style-id-columns.ts
 */

import { fetch as fetchRaw } from '@lib/fetch'
import { getDefinedUrlSearchParams } from '@lib/util'
import { backend } from '@services/services'

// Generic response wrapper
type ApiResponse<T> = {
  status: number
  data: T
  error?: unknown
  metadata?: {
    success: boolean
    count?: number
  }
}

// ============================================================================
// Types for Orders Routes
// ============================================================================

/**
 * Product information returned from cascade-tracing orders endpoints.
 * Matches the legacy PRODUCTS response format for backward compatibility.
 * Backend now queries STYLES table and transforms to this format.
 */
export interface IOrderProduct {
  id: string
  brandId: string | null
  name: string
  code: string | null
  companyName: string | null
  companyId: string | null
}

// Alias for backward compatibility (frontend may use either name)
export type IOrderStyle = IOrderProduct

// Order line style linking (renamed from product linking)
export interface IOrderLineStyleLinkingBody {
  styleId: string
  meta: {
    type: 'PURCHASE_ORDER' | 'SALES_ORDER'
    orderLineId: string
  }
}

// Backward compatible alias
export interface IOrderLineProductLinkingBody {
  productId: string // Will be treated as styleId on backend
  meta: {
    type: 'PURCHASE_ORDER' | 'SALES_ORDER'
    orderLineId: string
  }
}

export interface IOrderLineProductLinkingResponse {
  success: boolean
}

// Style KG quantity calculation (renamed from product)
export interface IStyleKgQuantityBody {
  styleId: string
  weight: {
    quantity: number
    unit: string
  }
}

// Backward compatible alias
export interface IProductKgQuantityBody {
  productId: string // Will be treated as styleId on backend
  weight: {
    quantity: number
    unit: string
  }
}

export interface IProductKgQuantityResponse {
  value: number | null
  unit: string
}

// Style stories recalculation
export interface IProductStoriesRecalculationBody {
  ids: string[] // Style IDs
}

export interface IProductStoriesRecalculationQuery {
  isProductComponent?: string
}

export interface IProductStoriesRecalculationResponse {
  success: boolean
}

// Style trace node (replaces product trace)
export interface IStyleTraceNode {
  id: string
  nodeId: string
  orderLineId: string | null
  styleId: string | null
  styleName: string | null
  styleCode: string | null
  styleTypeId: string | null
  forCompanyId: string | null
  companyName: string | null
  quantity: number | null
  unit: string | null
  secondaryQuantity: number | null
  secondaryUnit: string | null
  assetType: string
  assetId: string
  assetName: string | null
  assetCountry: string | null
  facilityProcesses: string[] | null
  featuredFacilityProcess: string | null
  depth: number | null
  children?: IStyleTraceNode[]
}

// Backward compatible alias (frontend may still use productId naming)
export interface IProductTraceNode {
  id: string
  nodeId: string
  orderLineId: string | null
  productId: string | null // Maps to styleId
  productName: string | null // Maps to styleName
  productCode: string | null // Maps to styleCode
  productBrandId: string | null // Maps to forCompanyId
  productBrandName: string | null // Maps to companyName
  quantity: number | null
  unit: string | null
  secondaryQuantity: number | null
  secondaryUnit: string | null
  assetType: string
  assetId: string
  assetName: string | null
  assetCountry: string | null
  facilityProcesses: string[] | null
  featuredFacilityProcess: string | null
  depth: number | null
  children?: IProductTraceNode[]
}

export interface IProductTraceResponse {
  graph: IProductTraceNode[]
}

export interface IProductTraceQuery {
  styleId?: string
}


// ============================================================================
// Types for Transactions Routes
// ============================================================================

export type TraceType = 'PURCHASE_ORDER' | 'SALES_ORDER'

// Node product trace
export interface INodeProductTraceQuery {
  orderId: string
  type: TraceType
}

// Product transformations
export interface IProductTransformationsQuery {
  orderId: string
  type: TraceType
}

// Product line link
export interface IProductLineLinkQuery {
  orderId: string
  type: TraceType
}

// Create product transformation (v2 naming: styleId instead of productId)
export interface IProductTransformationBody {
  nodeOrderlineId: string
  orderId: string
  type: TraceType
  inputStyleId: string
  inputQuantity: number
  inputUnit: string
  outputStyleId: string
  outputQuantity: number
  outputUnit: string
}

// Bulk product transformation (v2 naming: styleId instead of productId)
export interface IBulkProductTransformationBody {
  nodeOrderlineId: string
  orderId: string
  type: TraceType
  transformations: Array<{
    inputStyleId: string
    inputQuantity: number
    inputUnit: string
    outputStyleId: string
    outputQuantity: number
    outputUnit: string
  }>
}

// Attach origin (v2 naming: styleId instead of productId)
export interface IAttachOriginLineBody {
  styleId: string
  quantity: number
  unit: string
  secondaryQuantity?: number
  secondaryUnit?: string
}

export interface IAttachOriginBody {
  nodeId: string
  meta: {
    orderId: string
    type: TraceType
  }
  facilityProcesses: string[]
  countryCode?: string
  lines: IAttachOriginLineBody[]
  documents: Array<{
    fileId: string
    type: string
    name?: string
    reference?: string
    description?: string
  }>
  attachmentTemplateId?: string | null
}

// Attach order to tracing node (v2 naming: styleId in lines)
export interface IAttachOrderLineBody {
  styleId: string
  quantity: number
  unit?: string
  secondaryQuantity?: number
  secondaryUnit?: string
}

export interface IAttachOrderBody {
  nodeId: string
  orderNumber?: string
  receiver?: { id: string }
  meta: {
    orderId: string
    type: TraceType
  }
  lines?: IAttachOrderLineBody[]
}

// ============================================================================
// API Functions - Orders
// ============================================================================

const fetch = (url: string, ...args: any[]) => fetchRaw([backend.url, url], ...args)

/**
 * Get all styles on purchase orders by company
 * Route: GET /cascade-tracing/orders/purchases/products
 */
export const purchaseOrderProductsGet = (): Promise<ApiResponse<IOrderProduct[]>> => {
  console.log('Fetching purchase order products')
  return fetch(`/cascade-tracing/orders/purchases/styles`, {
    method: 'GET',
  })
}

/**
 * Get all product names on sales orders by company
 * Route: GET /cascade-tracing/orders/sales/products
 */
export const salesOrderProductsGet = (): Promise<ApiResponse<IOrderProduct[]>> => {
  return fetch(`/cascade-tracing/orders/sales/styles`, {
    method: 'GET',
  })
}

/**
 * Create order line product linking
 * Route: POST /cascade-tracing/orders/order-lines/product-linking
 */
export const cascadeTracingLinkOrderLineProduct = (
  body: IOrderLineProductLinkingBody
): Promise<V2ApiResponse<IOrderLineProductLinkingResponse>> => {
  return v2Fetch(`/cascade-tracing/orders/order-lines/product-linking`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

/**
 * Calculate product quantity in KG
 * Route: POST /cascade-tracing/orders/calculations/product-kg
 * Transforms payload: { productId, weight: { unit, quantity } } → { productId, unit, quantity }
 * Transforms response: { metadata, data: { quantity, unit } } → { status, data: { value } }
 */
export const calculateSecondaryWeight = async (
  data: IProductKgQuantityBody
): Promise<V2ApiResponse<IProductKgQuantityResponse>> => {
  const payload = {
    productId: data.productId,
    unit: data.weight?.unit,
    quantity: Number(data.weight?.quantity),
  }
  const response = await v2Fetch<{quantity: number; unit: string}>('/cascade-tracing/orders/calculations/product-kg', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  if (response.data) {
    return { ...response, data: { value: response.data.quantity } } as V2ApiResponse<IProductKgQuantityResponse>
  }
  return response as unknown as V2ApiResponse<IProductKgQuantityResponse>
}

/**
 * Recalculate product stories (assign deepest orders as product stories)
 * Route: POST /cascade-tracing/orders/product-stories-recalculation
 */
export const assignDeepestOrdersAsProductStories = (
  body: IProductStoriesRecalculationBody,
  queryParams: IProductStoriesRecalculationQuery = {}
): Promise<ApiResponse<IProductStoriesRecalculationResponse>> => {
  return fetch(
    `/cascade-tracing/orders/calculations/product-stories?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );
}

/**
 * Get product tracing by purchase order ID
 * Route: GET /cascade-tracing/orders/product-trace/purchases/:id
 */
export const cascadeTracingProductTracingGetByPurchaseOrderId = (
  id: string,
  queryParams: IProductTraceQuery = {}
): Promise<ApiResponse<IProductTraceResponse>> => {
  const queryString = getDefinedUrlSearchParams(queryParams)
  const url = queryString
    ? `/cascade-tracing/orders/product-trace/purchases/${id}?${queryString}`
    : `/cascade-tracing/orders/product-trace/purchases/${id}`
  return fetch(url, {
    method: 'GET',
  })
}

/**
 * Get product tracing by sales order ID
 * Route: GET /cascade-tracing/orders/product-trace/sales/:id
 */
export const cascadeTracingProductTracingGetBySalesOrderId = (
  id: string,
  queryParams: IProductTraceQuery = {}
): Promise<ApiResponse<IProductTraceResponse>> => {
  const queryString = getDefinedUrlSearchParams(queryParams)
  const url = queryString
    ? `/cascade-tracing/orders/product-trace/sales/${id}?${queryString}`
    : `/cascade-tracing/orders/product-trace/sales/${id}`
  return fetch(url, {
    method: 'GET',
  })
}

// ============================================================================
// API Functions - Transactions
// ============================================================================

/**
 * Get node product trace info
 * Route: GET /cascade-tracing/transactions/tracing-node-product-trace/:id
 */
export const cascadeTracingNodeProductTraceInfoGet = (
  id: string,
  queryParams: INodeProductTraceQuery
): Promise<ApiResponse<any>> => {
  return fetch(
    `/cascade-tracing/transactions/tracing-node-product-trace/${id}?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    }
  )
}

/**
 * Get product line link data
 * Route: GET /cascade-tracing/transactions/product-line-link/:id
 */
export const cascadeTracingProductTracingLineLinkGet = (
  id: string,
  queryParams: IProductLineLinkQuery
): Promise<ApiResponse<any>> => {
  return fetch(
    `/cascade-tracing/transactions/product-line-link/${id}?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    }
  )
}

/**
 * Get product node transformations
 * Route: GET /cascade-tracing/transactions/product-transformations/:nodeOrderlineId
 */
export const cascadeTracingProductTracingNodeProductTransformationGet = (
  id: string,
  queryParams: IProductTransformationsQuery
): Promise<ApiResponse<any>> => {
  return fetch(
    `/cascade-tracing/transactions/product-transformations/${id}?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    }
  )
}

/**
 * Create product transformation
 * Route: POST /cascade-tracing/transactions/product-transformation
 * Uses inputStyleId/outputStyleId instead of inputProductId/outputProductId
 */
export const productTransformationCreate = (
  body: IProductTransformationBody
): Promise<ApiResponse<any>> => {
  return fetch(`/cascade-tracing/transactions/product-transformation`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

/**
 * Bulk create product transformations
 * Route: POST /cascade-tracing/transactions/bulk-product-transformation
 * Uses inputStyleId/outputStyleId instead of inputProductId/outputProductId
 */
export const bulkProductTransformationCreate = (
  body: IBulkProductTransformationBody
): Promise<ApiResponse<any>> => {
  return fetch(`/cascade-tracing/transactions/bulk-product-transformation`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

/**
 * Attach origin to tracing
 * Route: POST /cascade-tracing/transactions/attach-origin
 * Uses styleId instead of productId
 */
export const cascadeTracingAttachOrigin = (
  body: IAttachOriginBody
): Promise<ApiResponse<any>> => {
  return fetch(`/cascade-tracing/transactions/attach-origin`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

/**
 * Attach order to tracing node
 * Route: POST /cascade-tracing/transactions/attach-order
 * Uses styleId in lines instead of productId
 */
export const cascadeTracingAttachOrder = (
  body: IAttachOrderBody
): Promise<V2ApiResponse<any>> => {
  return v2Fetch(`/cascade-tracing/transactions/attach-order`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

/**
 * Attach existing order to tracing node
 * Route: POST /cascade-tracing/transactions/attach-existing-order
 */
export const cascadeTracingAttachExistingOrder = (
  body: any
): Promise<V2ApiResponse<any>> => {
  return v2Fetch(`/cascade-tracing/transactions/attach-existing-order`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

/**
 * Get tracing link info by ID
 * Route: GET /cascade-tracing/transactions/link/:id
 * Returns link with nodes, transactions, documents, and origin order lines
 * with style data (product→style transformation applied)
 */
export const getTransactionLink = (
  linkId: string
): Promise<V2ApiResponse<any>> => {
  return v2Fetch(`/cascade-tracing/transactions/link/${linkId}`, {
    method: 'GET',
  })
}

// ============================================================================
// API Functions - Phase 1 Extended Routes (Category A, B, C)
// ============================================================================

/**
 * Body parameters for listing orders (POST /list endpoints)
 * Matches backend ListPurchaseOrdersQuerySchema / ListSalesOrdersQuerySchema
 */
export interface IListOrdersQuery {
  page?: number          // Default: 1, Min: 1
  limit?: number         // Default: 15, Min: 1, Max: 500
  sort?: string          // Sort field (e.g., "orderNumber", "orderDate", "status")
  order?: 'asc' | 'desc' | 'ASC' | 'DESC'  // Sort direction, Default: "desc"
  filter?: string        // General text search (max 512 chars)
  isArchived?: boolean   // Filter by archived status
  hasOrderNumber?: boolean  // Filter by presence of order number
  // Column filters
  orderNumber?: string
  tracingSalesOrderNumber?: string
  tracingPurchaseOrderNumber?: string
  supplierCompanyId?: string
  buyerCompanyId?: string
  status?: string
  lastProductionProcessId?: string
  orderType?: string
  receiverCompanyId?: string
  tags?: string
  productId?: string
}

/**
 * Get purchase order by ID
 * Route: GET /cascade-tracing/orders/purchases/:id
 * Returns full purchase order with lines[].product data from STYLES table
 */
export const cascadeTracingPurchaseOrderGet = (
  purchaseOrderId: string
): Promise<ApiResponse<any>> => {
  return fetch(`/cascade-tracing/orders/purchases/${purchaseOrderId}`, {
    method: 'GET',
  })
}

/**
 * Get sales order by ID
 * Route: GET /cascade-tracing/orders/sales/:id
 * Returns full sales order with lines[].product data from STYLES table
 */
export const cascadeTracingSalesOrderGet = (
  salesOrderId: string
): Promise<ApiResponse<any>> => {
  return fetch(`/cascade-tracing/orders/sales/${salesOrderId}`, {
    method: 'GET',
  })
}

/**
 * Get list of purchase orders
 * Route: POST /cascade-tracing/orders/purchases/list
 * Supports filter with styleId (replaces productId)
 */
export const cascadeTracingPurchaseOrdersGet = (
  body: IListOrdersQuery = {}
): Promise<ApiResponse<any>> => {
  return fetch(`/cascade-tracing/orders/purchases/list`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

/**
 * Get list of sales orders
 * Route: POST /cascade-tracing/orders/sales/list
 * Supports filter with styleId (replaces productId)
 */
export const cascadeTracingSalesOrdersGet = (
  body: IListOrdersQuery = {}
): Promise<ApiResponse<any>> => {
  return fetch(`/cascade-tracing/orders/sales/list`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

/**
 * Create purchase order
 * Route: POST /cascade-tracing/orders/purchases
 * Request body lines[].styleId is validated against STYLES table
 */
export const cascadeTracingPurchaseOrderCreate = (
  body: any
): Promise<ApiResponse<any>> => {
  return fetch(`/cascade-tracing/orders/purchases`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

/**
 * Create sales order
 * Route: POST /cascade-tracing/orders/sales
 * Request body lines[].styleId is validated against STYLES table
 */
export const cascadeTracingSalesOrderCreate = (
  body: any
): Promise<ApiResponse<any>> => {
  return fetch(`/cascade-tracing/orders/sales`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

/**
 * Update purchase order by ID
 * Route: PUT /cascade-tracing/orders/purchases/:id
 * Request body lines[].styleId is validated against STYLES table
 */
export const cascadeTracingPurchaseOrderEdit = (
  id: string,
  body: any
): Promise<ApiResponse<any>> => {
  return fetch(`/cascade-tracing/orders/purchases/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}

/**
 * Update sales order by ID
 * Route: PUT /cascade-tracing/orders/sales/:id
 * Request body lines[].styleId is validated against STYLES table
 */
export const cascadeTracingSalesOrderEdit = (
  id: string,
  body: any
): Promise<ApiResponse<any>> => {
  return fetch(`/cascade-tracing/orders/sales/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}

// ============================================================================
// V2 API Functions (unwrapped response)
// ============================================================================
// These functions use an unwrapping fetch that extracts data from the { metadata, data }
// response wrapper returned by the new /cascade-tracing/ endpoints.

// Unwrapped response type
interface V2ApiResponse<T> {
  data: T | null
  error: { message?: string; reqId?: string } | null
  status: number
}

// Raw backend response shape (has metadata wrapper)
interface BackendResponse<T> {
  metadata?: { success: boolean }
  data?: T
}

/**
 * Fetch helper that unwraps the { metadata, data } wrapper from the
 * new /cascade-tracing/ API endpoints, returning just the inner data.
 */
const v2Fetch = async <T>(url: string, options: RequestInit): Promise<V2ApiResponse<T>> => {
  const response = await fetchRaw([backend.url, url], options)

  if (response.error || !response.data) {
    return response as V2ApiResponse<T>
  }

  const backendData = response.data as BackendResponse<T>
  if (backendData && typeof backendData === 'object' && 'data' in backendData) {
    return {
      ...response,
      data: backendData.data ?? null,
    }
  }

  return response as V2ApiResponse<T>
}

const v2GetDefinedUrlSearchParams = (params: IListOrdersQuery): string => {
  const searchParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value))
    }
  })
  return searchParams.toString()
}

// --- Purchase Orders ---

export const getPurchaseOrder = (
  purchaseOrderId: string
): Promise<V2ApiResponse<any>> => {
  return v2Fetch(`/cascade-tracing/orders/purchases/${purchaseOrderId}`, {
    method: 'GET',
  })
}

export const getPurchaseOrdersByNode = (
  queryParams: Record<string, any>
): Promise<V2ApiResponse<any>> => {
  return v2Fetch(`/cascade-tracing/orders/purchases/by-node?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  })
}

export const getPurchaseOrders = (
  body: IListOrdersQuery = {}
): Promise<ApiResponse<any>> => {
  return fetch(`/cascade-tracing/orders/purchases/list`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export const getPurchaseOrderTags = (): Promise<ApiResponse<string[]>> => {
  return fetch(`/cascade-tracing/orders/purchases/tags`, {
    method: 'GET',
  })
}

export const createPurchaseOrder = (
  body: any
): Promise<V2ApiResponse<any>> => {
  return v2Fetch(`/cascade-tracing/orders/purchases`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export const updatePurchaseOrder = (
  id: string,
  body: any
): Promise<V2ApiResponse<any>> => {
  return v2Fetch(`/cascade-tracing/orders/purchases/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}

export const archivePurchaseOrder = (id: string): Promise<V2ApiResponse<void>> => {
  return v2Fetch(`/cascade-tracing/orders/purchases/${id}/archive`, {
    method: 'POST',
  })
}

export const unarchivePurchaseOrder = (id: string): Promise<V2ApiResponse<void>> => {
  return v2Fetch(`/cascade-tracing/orders/purchases/${id}/unarchive`, {
    method: 'POST',
  })
}

// --- Sales Orders ---

export const getSalesOrder = (
  salesOrderId: string
): Promise<V2ApiResponse<any>> => {
  return v2Fetch(`/cascade-tracing/orders/sales/${salesOrderId}`, {
    method: 'GET',
  })
}

export const getSalesOrders = (
  body: IListOrdersQuery = {}
): Promise<ApiResponse<any>> => {
  return fetch(`/cascade-tracing/orders/sales/list`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export const createSalesOrder = (
  body: any
): Promise<V2ApiResponse<any>> => {
  return v2Fetch(`/cascade-tracing/orders/sales`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export const updateSalesOrder = (
  id: string,
  body: any
): Promise<V2ApiResponse<any>> => {
  return v2Fetch(`/cascade-tracing/orders/sales/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}


// --- Internal Orders ---

export const getInternalOrders = (
  body: IListOrdersQuery = {}
): Promise<ApiResponse<any>> => {
  return fetch(`/cascade-tracing/orders/internal/list`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

// --- Trace Graph ---

export const getTraceGraphByPurchaseOrderId = (
  purchaseOrderId: string
): Promise<V2ApiResponse<any>> => {
  return v2Fetch(`/cascade-tracing/orders/trace/purchases/${purchaseOrderId}`, {
    method: 'GET',
  })
}

export const getTraceGraphBySalesOrderId = (
  salesOrderId: string
): Promise<V2ApiResponse<any>> => {
  return v2Fetch(`/cascade-tracing/orders/trace/sales/${salesOrderId}`, {
    method: 'GET',
  })
}

export const cascadeTracingExportByPurchaseOrderIds= (purchaseOrderIds: string[]): Promise<V2ApiResponse<any>> => {
  return v2Fetch(`/cascade-tracing/orders/trace/purchase/bulk-export`, {
    method: 'POST',
    body: JSON.stringify(purchaseOrderIds),
  })
}

export const cascadeTracingExportBySalesOrderIds= (salesOrderIds: string[]): Promise<V2ApiResponse<any>> => {
  return v2Fetch(`/cascade-tracing/orders/trace/sales/bulk-export`, {
    method: 'POST',
    body: JSON.stringify(salesOrderIds),
  })
}